'use client'

import { sendGTMEvent } from '@next/third-parties/google'
import clsx from 'clsx'
import { SubBrandName } from 'lib/settings/brands/index'
import { subBrands } from 'lib/settings/brands/sub-brands'
import { emailPattern } from 'lib/utilities/string'
import { MouseEvent, ReactNode, useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import Button from '../Button/Button'
import CheckboxInput from '../Form/CheckboxInput/CheckboxInput'
import TextInput from '../Form/TextInput/TextInput'
import Header from '../Header/Header'
import Icon from '../Icon/Icon'
import Link from '../Link/Link'
import SmsOptInModal from '../SmsOptInModal/SmsOptInModal'

// Import the API hook
import useUserSubscriptionsApi from '@/api/useUserSubscriptionsApi'
import { useRecaptchaV3 } from '@/hooks/useRecaptchaV3'
import { useRudderstackAnalytics } from '@/hooks/useRudderstackAnalytics'
import { useSmsMarketingEnabled } from '@/hooks/useSmsMarketingEnabled'
import { queueFooterSmsOptInModal } from '@/utils/smsOptInModalQueue'

interface SampleFormDataType {
  email?: string
}

export interface NewsletterSignupProps {
  /**
   * The title of the newsletter signup.
   */
  title: string | ReactNode
  /**
   * The subtitle of the newsletter signup.
   */
  subtitle?: string | ReactNode
  /**
   * The brand to use.
   */
  brand: SubBrandName
  /**
   * The background theme class.
   */
  backgroundTheme?: string
}

export default function NewsletterSignup({
  title,
  subtitle,
  brand = 'shipplay',
  backgroundTheme = 'bg-theme-accent-3',
}: NewsletterSignupProps) {
  const { ref } = useRudderstackAnalytics()
  const { executeRecaptcha } = useRecaptchaV3()
  const { enabled: isSmsMarketingOn } = useSmsMarketingEnabled()

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
  } = useForm<SampleFormDataType>()
  const [isSuccess, setIsSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Use the subscription API hook
  const subscriptionMutation = useUserSubscriptionsApi()

  const stopPropagation = useCallback((e: MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
  }, [])

  const subBrand = subBrands[brand]

  const onSubmit = useCallback(
    async (data: SampleFormDataType) => {
      if (!data.email) return

      setErrorMessage(null)

      // Mint a reCAPTCHA v3 token before the mutation. The action name must
      // match the backend's verify_recaptcha(action: 'user_subscriptions')
      // call so Google grades the score against the right action threshold.
      const recaptchaToken = await executeRecaptcha('user_subscriptions')
      if (!recaptchaToken) {
        setErrorMessage('Verification failed. Please refresh and try again.')
        return
      }

      subscriptionMutation.mutate(
        {
          email: data.email,
          anonymous_id: ref?.current?.getAnonymousId(),
          'g-recaptcha-response': recaptchaToken,
        },
        {
          onSuccess: () => {
            setIsSuccess(true)

            sendGTMEvent({
              event: 'generate_lead',
              method: 'Footer',
              user_email: data.email,
            })

            // Prompt for the SMS program once the email opt-in has landed,
            // seeding the modal with the email for backend identity stitching.
            // Queued with a short hold so the inline success state above
            // registers before the modal takes over the screen.
            queueFooterSmsOptInModal(data.email)
          },
          onError: (error) => {
            setErrorMessage(
              error.message || 'Failed to subscribe. Please try again.',
            )
          },
        },
      )
    },
    [executeRecaptcha, ref, subscriptionMutation],
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div
        className={`${backgroundTheme} min-h-[252px] flex justify-center items-center rounded-2xl px-[1.1875rem] md:px-[2.5625rem] lg:px-[4.5rem] py-6 md:py-10 lg:py-16`}
      >
        <div className="w-full flex justify-center md:justify-between items-center flex-col md:flex-row gap-x-[2rem] lg:gap-x-12 gap-y-6 ">
          <div className="max-w-[15.43rem] lg:max-w-[30.25rem] w-full">
            <Header
              as="h2"
              overrideClassName
              className="type-headline-4 lg:type-headline-2 text-center md:text-left"
            >
              {title}
            </Header>
            {subtitle && (
              <p className="type-body-4 text-center md:text-left mb-4 lg:mb-6">
                {subtitle}
              </p>
            )}
          </div>
          {isSuccess ? (
            <div className="flex items-center gap-3">
              <Icon
                icon="check-circle-outline"
                className="w-5 h-5 lg:w-9 lg:h-9"
              />
              <span className="type-body-2 lg:type-headline-4">
                Thank you for signing up!
              </span>
            </div>
          ) : (
            <div className="max-w-[30rem] lg:max-w-[35rem] w-full">
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_0.37fr] lg:grid-rows-[auto_auto] lg:grid-flow-col gap-x-6 w-full">
                <div>
                  <TextInput
                    name="email"
                    type="email"
                    control={control}
                    placeholder="Enter email address"
                    className="w-full lg:max-w-[20.875rem] [&_p]:text-neutral-700 [&_p]:mt-2 [&_input]:m-0 mx-auto [&_input]:shadow-none [&_.text-input-content]:my-0"
                    rules={{
                      required: { value: true, message: 'Address is required' },
                      pattern: {
                        value: emailPattern,
                        message: 'Please enter valid input',
                      },
                    }}
                  />
                </div>
                <div className="lg:col-span-2 [&_.text-error]:hidden">
                  <CheckboxInput
                    label={
                      <>
                        I agree to {subBrand.name}{' '}
                        <Link
                          onClick={stopPropagation}
                          target="_blank"
                          rel="noreferrer"
                          className="hover:underline no-underline !h-auto !px-0"
                          href="/terms"
                          inheritFont
                        >
                          Terms of Use
                        </Link>{' '}
                        and{' '}
                        <Link
                          onClick={stopPropagation}
                          target="_blank"
                          rel="noreferrer"
                          className="hover:underline no-underline !h-auto !px-0"
                          href="/privacy"
                          inheritFont
                        >
                          Privacy Policy
                        </Link>
                        .
                      </>
                    }
                    name="i_agree"
                    rules={{
                      required: {
                        value: true,
                        message: 'Must agree to proceed',
                      },
                    }}
                    className={clsx(
                      '!inline-flex mt-6 sm-max:text-[10px] md:mt-2 text-theme-primary font-semibold [&_.shadow-input-active]:shadow-[inset_0_0_0_2px_rgb(37,_37,_37)]',
                    )}
                    control={control}
                  />
                </div>
                <div className="text-center lg:text-left mt-[1.5rem] md:mt-[1rem] lg:mt-0">
                  {errorMessage && (
                    <p className="text-red-600 text-sm mb-2">{errorMessage}</p>
                  )}
                  <Button
                    type="submit"
                    className="w-[12.75rem] md:w-full lg:w-[12.5rem]"
                    disabled={
                      !isDirty || !isValid || subscriptionMutation.isPending
                    }
                  >
                    {subscriptionMutation.isPending
                      ? 'Subscribing...'
                      : 'Submit'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Gated on the runtime per-brand SMS marketing flag. */}
      {isSmsMarketingOn && <SmsOptInModal brand={brand} source="FOOTER" />}
    </form>
  )
}
